import React, { useState } from 'react';
import { Alert, Button, Col, Row, Table, Tag, Typography } from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import useToastContext from '../../hooks/useToastContext';
import usePutRequestUpgrade from '../../hooks/utils/usePutRequestUpgrade';
import { validateStatus } from '../../utils/utils';
import ModalCharges, { ChargesFormInputs } from './modal-charges';
import { SectionTitleActions, SectionTitleContainer } from '../styles';

const Charges = ({
  charges,
  setCharges,
  cardAffinityGroupId,
  action,
}: {
  charges: ChargesFormInputs[];
  setCharges?: (values: ChargesFormInputs[]) => void;
  cardAffinityGroupId?: string | undefined;
  setNextStep?: (value: boolean) => void;
  action?: string | undefined;
}) => {
  const [chargesOpen, setChargesOpen] = useState(false);
  const [editCharge, setEditCharge] = useState<ChargesFormInputs>(null);
  const { putError, executePut } = usePutRequestUpgrade({ path: `v1/card_affinity_groups/${cardAffinityGroupId}/charges` });
  const toast = useToastContext();

  const onDeleteCharge = async (value: ChargesFormInputs) => {

    if(cardAffinityGroupId !== undefined && cardAffinityGroupId !== null) {
      const chargesWithoutDeleted = charges.filter((item) => !(item.value === value?.value && item.type === value?.type ));
      try {
        const response = await executePut({ data: { charges: chargesWithoutDeleted } });
        if (validateStatus(response?.status)) {
          toast.success('Exito', 'Cargo eliminado correctamente!.');
          setCharges(chargesWithoutDeleted);
        } else {
          throw new Error();
        }

      } catch (e: unknown) {
        toast.error('Error', `Ocurrió un error: ${putError}, porfavor, intenta nuevamente.`);
        onDeleteCharge(value);
      }
    } else {
      setCharges(charges.filter((item) => !(item.value === value?.value && item.type === value?.type )));
    }
  }

  const onCreateCharges = async (values: any) => {
    const newCharges = [...(charges || []), values];

    setCharges(newCharges);
    setChargesOpen(false);

    return newCharges;
  }

  const parseNewValue = (newValue: ChargesFormInputs): ChargesFormInputs => {
    return {
      ...newValue,
      value: Number(newValue.value)
    };
  };

  const onUpdateCharges = async (value: ChargesFormInputs, newValue: ChargesFormInputs) => {
    const chargesFiltered = charges.filter((item) => !(item.value === value?.value && item.type === value?.type ));
    chargesFiltered.push(parseNewValue(newValue));
    setCharges(chargesFiltered);

    return chargesFiltered;
  }

  const validateAction = async (value: ChargesFormInputs) => {
    if (cardAffinityGroupId !== undefined && cardAffinityGroupId !== null) {

      if(editCharge) {
        const newCharges = await onUpdateCharges(editCharge, value);
        try {
          const response = await executePut({ data: { charges: newCharges } });
          if (validateStatus(response?.status)) {
            toast.success('Exito', 'Cargo actualizado correctamente!.');
          } else {
            throw new Error();
          }

        } catch (e: unknown) {
          toast.error('Error', `Ocurrió un error: ${putError}, porfavor, intenta nuevamente.`);
          onDeleteCharge(value);
        }
      }else {
        const newCharges = await onCreateCharges(value);
        try {
          const response = await executePut({ data: { charges: newCharges } });
          if (validateStatus(response?.status)) {
            toast.success('Exito', 'Cargo creado correctamente!.');
          } else {
            throw new Error();
          }

        } catch (e: unknown) {
          toast.error('Error', `Ocurrió un error: ${putError}, porfavor, intenta nuevamente.`);
          onDeleteCharge(value);
        }
      }
      setEditCharge(null);
    } else {
      onCreateCharges(value);
    }

    setChargesOpen(false);
  }

  return (
    <>
      {
        charges?.length > 0 ? (
          <>
            <SectionTitleContainer>
              <Typography.Title level={3}>Cargos</Typography.Title>
              {
                action !== 'view' ? (
                  <SectionTitleActions>
                    <Button size="middle" type="link" onClick={() => setChargesOpen(true)}>
                      Agregar
                    </Button>
                  </SectionTitleActions>
                ) : ''
              }
            </SectionTitleContainer>
            <Row>
              <Col span={24}>
                <Table
                  scroll={{ x: true }}
                  dataSource={charges?.map((address, index) => ({
                    ...address,
                    id: index,
                  }))}
                  pagination={ false }
                  columns={[
                    { dataIndex: 'id', key: 'id', title: 'ID' },
                    { dataIndex: 'type', key: 'type', title: 'Tipo' },
                    { dataIndex: 'value', key: 'value', title: 'Valor' },
                    {
                      dataIndex: 'taxes',
                      key: 'taxes',
                      title: 'Impuestos',
                      render: (_, record) => {
                        return (
                          <>
                            {
                              record?.taxes?.map( (tax) => {
                                return (
                                  <Tag
                                    key={tax.name}
                                    bordered={false}
                                    color="purple"
                                    style={{ cursor: 'pointer' }}
                                  >
                                    { tax.name }
                                  </Tag>
                                )
                              })
                            }
                          </>
                        )
                      }
                    },
                    {
                      dataIndex: 'actions',
                      key: 'actions',
                      title: 'Acciones',
                      // eslint-disable-next-line @typescript-eslint/no-unused-vars
                      render: (_, record) => {
                        return (
                          <>
                            {
                              action !== 'view' ?
                                cardAffinityGroupId !== undefined && cardAffinityGroupId !== null ? (
                                  <Row style={{ justifyContent: 'right' }}>
                                    <Button
                                      icon={<EditOutlined />}
                                      size="small"
                                      type="link"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        setEditCharge(record);
                                        setChargesOpen(!chargesOpen);
                                      }}
                                    />
                                    <Button
                                      icon={<DeleteOutlined />}
                                      size="small"
                                      type="link"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        onDeleteCharge(record);
                                      }}
                                    />
                                  </Row>
                                ) : (
                                  <Row style={{ justifyContent: 'right' }}>
                                    <Button
                                      icon={ <DeleteOutlined /> }
                                      size="small"
                                      type="link"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        onDeleteCharge(record);
                                      }}
                                    />
                                  </Row>
                                )
                                : ''
                            }
                          </>
                        );
                      },
                    },
                  ]}
                ></Table>
              </Col>
            </Row>
          </>
        ) : (
          <Row gutter={24}>
            <Col span={12}>
              <Typography.Title level={3}>Cargos</Typography.Title>
            </Col>
            <Col span={12} style={{ textAlign: 'right' }}>
              <Button size="small" type={"link"} onClick={() => setChargesOpen(true)}>
                Agregar
              </Button>
            </Col>
            <Col span={24}>
              <Alert
                message="No tienes cargos para mostrar"
                type="info"
                showIcon
                style={{ marginBottom: '2rem' }}
              />
            </Col>
          </Row>
        )
      }
      <ModalCharges
        charge={ editCharge }
        open={ chargesOpen }
        onCreate={ validateAction }
        onCancel={ () => setChargesOpen(false) }
      />
    </>
  );
}

export default Charges;